import { type ReactNode, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { StockModel } from '@/http/models/Stock/Stock'
import { ProductVariationModel } from '@/types/model/ProductVariation'
import { Spinner } from '@/components/ui/spinner'

const NEW_BATCH = '__new_batch__'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  variationId: string
  stocks: ProductVariationModel.VariationStock[]
  onSuccess?: () => void
  /** Values the assistant prepared: the batch (null = a new batch) and the new quantity. */
  prefill?: { batchId: string | null; quantity: number }
  /** Shown above the fields while reviewing a prepared change. */
  notice?: ReactNode
}

const formatExpiry = (date: string | null | undefined) =>
  date ? new Date(date).toLocaleDateString() : 'No expiry'

const sortedByExpiry = (stocks: ProductVariationModel.VariationStock[]) =>
  stocks.slice().sort((a, b) => {
    if (!a.expiry_date && !b.expiry_date) return 0
    if (!a.expiry_date) return 1
    if (!b.expiry_date) return -1
    return new Date(a.expiry_date).getTime() - new Date(b.expiry_date).getTime()
  })

export const VariantStockAdjustDialog = ({ open, onOpenChange, variationId, stocks, onSuccess, prefill, notice }: Props) => {
  const [selectedValue, setSelectedValue] = useState<string>('')
  const [quantity, setQuantity] = useState<string>('')
  const [expiryDate, setExpiryDate] = useState<string>('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open || !prefill) return
    setSelectedValue(prefill.batchId ?? NEW_BATCH)
    setQuantity(String(prefill.quantity))
  }, [open, prefill])

  const hasStocks = stocks.length > 0
  const isNewBatch = !hasStocks || selectedValue === NEW_BATCH
  const selectedStock = hasStocks ? stocks.find((s) => s._id === selectedValue) : null

  const canSubmit =
    quantity !== '' &&
    !isNaN(parseInt(quantity, 10)) &&
    (isNewBatch || !!selectedStock)

  const reset = () => {
    setSelectedValue('')
    setQuantity('')
    setExpiryDate('')
  }

  const handleClose = (val: boolean) => {
    if (!loading) {
      onOpenChange(val)
      if (!val) reset()
    }
  }

  const handleSubmit = async () => {
    const qty = parseInt(quantity, 10)
    if (isNaN(qty)) return

    setLoading(true)
    try {
      let response: any

      if (isNewBatch) {
        response = await StockModel.purchase({
          body: {
            description: '',
            image_ids: [],
            entries: [
              {
                product_variation_id: variationId,
                quantity: qty,
                purchase_price: 0,
                sale_price: 0,
                ...(expiryDate ? { expiry_date: expiryDate } : {}),
              },
            ],
          },
        })
      } else {
        response = await StockModel.adjust({
          body: {
            entries: [
              {
                product_variation_id: variationId,
                stock_purchase_transaction_id: selectedStock!.stock_purchase_transaction_id,
                quantity: qty,
              },
            ],
          },
        })
      }

      if (!response.success) {
        throw new Error((response.error?.data as any)?.message || 'Operation failed')
      }

      toast.success('Stock updated successfully')
      onSuccess?.()
      handleClose(false)
    } catch (err: any) {
      toast.error(err.message || 'Failed to update stock')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Update stock</DialogTitle>
        </DialogHeader>
        {notice}

        <div className="space-y-4">
          {hasStocks && (
            <div className="space-y-1.5">
              <Label>Batch</Label>
              <Select value={selectedValue} onValueChange={setSelectedValue}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a batch" />
                </SelectTrigger>
                <SelectContent>
                  {sortedByExpiry(stocks).map((stock) => (
                    <SelectItem key={stock._id} value={stock._id}>
                      Qty: {stock.quantity} — Expiry: {formatExpiry(stock.expiry_date)}
                    </SelectItem>
                  ))}
                  <SelectItem value={NEW_BATCH}>+ New batch</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {isNewBatch && (
            <div className="space-y-1.5">
              <Label>Expiry date <span className="text-muted-foreground">(optional)</span></Label>
              <Input
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Quantity</Label>
            <Input
              type="number"
              placeholder="Enter quantity"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || loading}>
            {loading ? (
              <>
                <Spinner />
                Saving…
              </>
            ) : (
              'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
